import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { format, parseISO } from 'date-fns'

import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { ConcurrentCareEpisode, Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'

import { getSelectedClientId } from '../../../../../data/lyraTherapy/clientSelectors'
import { useGetConcurrentCareQuery } from '../../../../episodes/data/concurrentCareApi'
import { useAllEpisodesForSelectedClient } from '../../../../episodes/data/hooks/useAllEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../../../../episodes/data/hooks/useSelectedEpisodeForClient'

const formatDateForConcurrentCare = (dateString: string): string => {
  try {
    return format(parseISO(dateString), 'MMM dd, yyyy')
  } catch {
    return dateString
  }
}

export const useConcurrentCareData = (): ConcurrentCareEpisode[] => {
  const intl = useIntl()
  const clientId = useSelector(getSelectedClientId)
  const { data: allEpisodes = [] } = useAllEpisodesForSelectedClient()
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }

  const { data: concurrentCare = [] } = useGetConcurrentCareQuery(
    { patientId: clientId },
    {
      skip: !clientId,
    },
  )

  return concurrentCare
    .filter((concurrentCareItem) => concurrentCareItem.episodeId !== selectedEpisode?.id)
    .map((concurrentCareItem) => {
      const episode = allEpisodes.find((episode) => episode.id === concurrentCareItem.episodeId)
      const programName = episode
        ? getProgramDisplayLabel(intl, episode?.program_name, 'clientProfile') || episode.program_name
        : ''
      const formattedStartDate = episode ? formatDateForConcurrentCare(episode.start_date) : ''
      const formattedNextSessionDate = concurrentCareItem.appointment?.startDateTime
        ? formatDateForConcurrentCare(concurrentCareItem.appointment?.startDateTime)
        : ''

      return {
        providerId: concurrentCareItem.provider.id,
        episodeStartDate: formattedStartDate,
        nextSessionDate: formattedNextSessionDate,
        providerImageUrl: concurrentCareItem.provider.imageUrl ?? undefined,
        providerLyraType: concurrentCareItem.provider.lyra_type ?? undefined,
        providerName: concurrentCareItem.provider.name,
        programName,
      }
    })
    .sort((a, b) => a.programName.localeCompare(b.programName))
}
